/**
 * MeetingHeader — sticky page header for the meeting review page.
 *
 * Contains:
 *   - Back button (chevron-left → navigate to "/")
 *   - EditableTitle + lock badge
 *   - Mobile "Review" toggle (CSS hides at ≥1280px)
 *   - Overflow menu (⋯ → Danger zone in Modal)
 *   - RecordedTimeEditor under the title row
 *
 * Status chip, Lock/Unlock, Re-annotate, and Export menu all moved to
 * JourneyStrip (Task 4 of the lifecycle-visualization plan).
 *
 * The header measures its own height and keeps --sticky-header-h in sync
 * so the PlayerDock and .turn scroll-margin-top always sit at the correct
 * offset (same logic as the old sticky-header in MeetingReview.tsx).
 */

import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { IconButton } from "../../ui/IconButton";
import { Button } from "../../ui/Button";
import { LockBadge } from "../../ui/LockBadge";
import { Modal } from "../../ui/Modal";
import { EditableTitle } from "../../components/EditableTitle";
import { RecordedTimeEditor } from "../../components/RecordedTimeEditor";
import { DangerZone } from "../../components/DangerZone";
import { t } from "../../i18n";

/**
 * Closes a dropdown (via `onClose`) when the user clicks outside `containerRef`
 * or presses Escape.  Only active when `isOpen` is true.
 */
function useOutsideClickEsc(
  containerRef: React.RefObject<HTMLElement | null>,
  isOpen: boolean,
  onClose: () => void,
) {
  useEffect(() => {
    if (!isOpen) return;
    function handleClick(e: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        onClose();
      }
    }
    function handleKey(e: KeyboardEvent) {
      if (e.key === "Escape") onClose();
    }
    document.addEventListener("mousedown", handleClick);
    document.addEventListener("keydown", handleKey);
    return () => {
      document.removeEventListener("mousedown", handleClick);
      document.removeEventListener("keydown", handleKey);
    };
  }, [containerRef, isOpen, onClose]);
}

// Re-export these predicates so the orchestrator can use them too
export function canReAnnotate(status: string): boolean {
  return status === "ready" || status === "error" || status === "annotating";
}

export function canLock(status: string, isLocked: boolean): boolean {
  if (isLocked) return true;
  return status === "ready";
}

interface Props {
  meeting: {
    id: string;
    title: string | null;
    status: string;
    recorded_at: string | null;
    title_recorded_at: string | null;
    locked_at: string | null;
    workspace_id: string;
    project_id: string | null;
  };
  isLocked: boolean;
  onTitleChange: (next: string | null) => void;
  onRecordedAtChange: (next: string) => void;
  /** Expose the header node so the parent can attach a ResizeObserver */
  headerRef: (node: HTMLDivElement | null) => void;
  /** Toggles the review-rail slide-over (button only visible below 1280px) */
  onToggleReview: () => void;
  // For DangerZone (inside overflow modal)
  onResubmitStart: () => string;
  onResubmitError: (prevStatus: string, e: unknown) => void;
}

export function MeetingHeader({
  meeting,
  isLocked,
  onTitleChange,
  onRecordedAtChange,
  headerRef,
  onToggleReview,
  onResubmitStart,
  onResubmitError,
}: Props) {
  const navigate = useNavigate();
  const [overflowOpen, setOverflowOpen] = useState(false);
  const [dangerModalOpen, setDangerModalOpen] = useState(false);
  const overflowRef = useRef<HTMLDivElement | null>(null);

  const closeOverflow = useCallback(() => setOverflowOpen(false), []);
  useOutsideClickEsc(overflowRef, overflowOpen, closeOverflow);

  return (
    <div
      ref={headerRef}
      className="sticky-header"
      style={{
        position: "sticky",
        // calc(-1 * var(--page-pad)) compensates main.page's padding so the
        // pinned header sits flush with the viewport top (no strip of transcript
        // text scrolling above it). marginInline mirrors the same value so the
        // header bleeds to the column edges. Both are driven by --page-pad so
        // the magic -24 constant lives in exactly one place.
        top: "calc(-1 * var(--page-pad, 24px))",
        zIndex: 11,
        background: "var(--bg)",
        borderBottom: "1px solid var(--border)",
        paddingBlock: 10,
        marginInline: "calc(-1 * var(--page-pad, 24px))",
        paddingInline: "var(--page-pad, 24px)",
        display: "flex",
        flexDirection: "column",
        gap: 4,
      }}
    >
      {/* Row 1: back + title + actions */}
      <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
        {/* Back */}
        <IconButton
          label="Back to library"
          name="chevron-left"
          size={18}
          onClick={() => navigate("/")}
          style={{ flexShrink: 0 }}
        />

        {/* Title + lock badge */}
        <div style={{ display: "flex", alignItems: "center", gap: 8, minWidth: 0, flex: 1 }}>
          <h2 style={{ margin: 0, minWidth: 0, flex: 1 }}>
            <EditableTitle
              meetingId={meeting.id}
              workspaceId={meeting.workspace_id}
              value={meeting.title}
              fallbackDate={meeting.recorded_at}
              onChange={onTitleChange}
            />
          </h2>
          {isLocked && (
            <span
              title={`Locked ${new Date(meeting.locked_at!).toLocaleString()}`}
              style={{ flexShrink: 0 }}
            >
              <LockBadge label />
            </span>
          )}
        </div>

        {/* Right-side actions */}
        <div className="sticky-header-actions" style={{ display: "flex", alignItems: "center", gap: 8, flexShrink: 0 }}>
          {/* Review (slide-over toggle) — CSS hides this at >=1280px where
              the rail is part of the page layout */}
          <Button
            variant="secondary"
            size="sm"
            className="review-toggle-btn"
            onClick={onToggleReview}
            title="Open the review panel"
          >
            Review
          </Button>

          {/* Overflow menu ⋯ — single menu item: Danger zone */}
          <div style={{ position: "relative" }} ref={overflowRef}>
            <button
              type="button"
              aria-label="More options"
              className="icon-btn-kit"
              onClick={() => setOverflowOpen((o) => !o)}
              style={{ fontFamily: "inherit", fontSize: 18, lineHeight: 1, padding: "4px 8px" }}
            >
              ⋯
            </button>
            {overflowOpen && (
              <div
                style={{
                  position: "absolute",
                  top: "calc(100% + 4px)",
                  insetInlineEnd: 0,
                  background: "var(--surface)",
                  border: "1px solid var(--border)",
                  borderRadius: 8,
                  boxShadow: "var(--shadow-md, 0 4px 12px rgba(0,0,0,0.4))",
                  zIndex: 30,
                  minWidth: 160,
                  overflow: "hidden",
                }}
                role="menu"
              >
                <button
                  type="button"
                  role="menuitem"
                  onClick={() => { setOverflowOpen(false); setDangerModalOpen(true); }}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 8,
                    width: "100%",
                    padding: "10px 14px",
                    background: "transparent",
                    border: "none",
                    borderRadius: 0,
                    color: "var(--danger, #dc2626)",
                    fontWeight: 400,
                    fontSize: 14,
                    cursor: "pointer",
                    textAlign: "start",
                  }}
                >
                  {t("meeting.dangerZone")}
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Row 2: RecordedTimeEditor */}
      <RecordedTimeEditor
        meetingId={meeting.id}
        recordedAt={meeting.recorded_at}
        titleRecordedAt={meeting.title_recorded_at}
        isLocked={isLocked}
        onSaved={onRecordedAtChange}
      />

      {/* Danger zone modal */}
      <Modal open={dangerModalOpen} onClose={() => setDangerModalOpen(false)}>
        <div style={{ padding: 4 }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
            <h3 style={{ margin: 0 }}>{t("meeting.dangerZone")}</h3>
            <IconButton label="Close" name="x" onClick={() => setDangerModalOpen(false)} />
          </div>
          <DangerZone
            meetingId={meeting.id}
            workspaceId={meeting.workspace_id}
            meetingStatus={meeting.status}
            isLocked={isLocked}
            onResubmitStart={onResubmitStart}
            onResubmitError={onResubmitError}
          />
        </div>
      </Modal>
    </div>
  );
}
